import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { NotebookPen, Pencil, Save, Loader2, ArrowRight, Check, Wine, Sparkles } from "lucide-react";
import TastingPhotoControls from "@/components/TastingPhotoControls";

const TXT = {
  fr: { suggest: "Suggestion IA — à ajuster", title: "Fiche de dégustation", edit: "Corriger", done: "Terminer", save: "Enregistrer dans mon carnet", saved: "Enregistrée dans votre carnet", view: "Voir dans mon carnet", login: "Créer un compte pour enregistrer", name: "Cuvée", year: "Millésime", producer: "Producteur", appellation: "Appellation", region: "Région", grapes: "Cépages", robe: "Robe", nez: "Nez", bouche: "Bouche", score: "Note /20", comment: "Commentaire", err: "Enregistrement impossible" },
  en: { suggest: "AI suggestion — adjust it", title: "Tasting sheet", edit: "Edit", done: "Done", save: "Save to my notebook", saved: "Saved to your notebook", view: "View in my notebook", login: "Create an account to save", name: "Cuvée", year: "Vintage", producer: "Producer", appellation: "Appellation", region: "Region", grapes: "Grapes", robe: "Colour", nez: "Nose", bouche: "Palate", score: "Score /20", comment: "Comment", err: "Could not save" },
  es: { suggest: "Sugerencia IA — ajústala", title: "Ficha de cata", edit: "Corregir", done: "Listo", save: "Guardar en mi cuaderno", saved: "Guardada en tu cuaderno", view: "Ver en mi cuaderno", login: "Crea una cuenta para guardar", name: "Cuvée", year: "Añada", producer: "Productor", appellation: "Denominación", region: "Región", grapes: "Uvas", robe: "Color", nez: "Nariz", bouche: "Boca", score: "Nota /20", comment: "Comentario", err: "No se pudo guardar" },
  pt: { suggest: "Sugestão IA — ajusta-a", title: "Ficha de prova", edit: "Corrigir", done: "Concluir", save: "Guardar no meu caderno", saved: "Guardada no teu caderno", view: "Ver no meu caderno", login: "Cria uma conta para guardar", name: "Cuvée", year: "Colheita", producer: "Produtor", appellation: "Denominação", region: "Região", grapes: "Castas", robe: "Cor", nez: "Nariz", bouche: "Boca", score: "Nota /20", comment: "Comentário", err: "Não foi possível guardar" },
};

const SHORT = ["name", "year", "producer", "appellation", "region", "grapes", "score"];
const LONG = ["robe", "nez", "bouche", "comment"];

function toForm(d) {
  return { ...d, grapes: (d.grapes || []).join(", "), year: d.year ?? "", score: d.score ?? "" };
}

function toPayload(f) {
  return {
    product_type: f.product_type || "Vin",
    name: (f.name || "").trim() || "Dégustation",
    year: f.year ? parseInt(f.year, 10) || null : null,
    producer: f.producer || null,
    region: f.region || null,
    appellation: f.appellation || null,
    grapes: (f.grapes || "").split(",").map((g) => g.trim()).filter(Boolean),
    robe: f.robe || null,
    nez: f.nez || null,
    bouche: f.bouche || null,
    score: f.score !== "" && f.score != null ? parseFloat(String(f.score).replace(",", ".")) : null,
    comment: f.comment || null,
    photo_url: f.photo_url || null,
    web_photo_url: f.web_photo_url || null,
    user_photo_url: f.user_photo_url || null,
    domain_url: f.domain_url || null,
    price_indication: f.price_indication || null,
    raw_notes: f.raw_notes || null,
  };
}

export default function TastingDraftCard({ draft, lang = "fr", onSaved }) {
  const T = TXT[lang] || TXT.fr;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(() => toForm(draft));
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isSuggested = (k) => (draft.suggested_fields || []).includes(k) && form[k] === draft[k];

  const save = async () => {
    if (!user) { navigate(`/register?next=${encodeURIComponent(window.location.pathname)}`); return; }
    setSaving(true);
    try {
      let f = form;
      if (String(f.user_photo_url || "").startsWith("data:")) {
        const up = await api.post("/tastings/photo", { image_base64: f.user_photo_url });
        f = { ...f, user_photo_url: up.data.photo_url, photo_url: f.photo_url === f.user_photo_url ? up.data.photo_url : f.photo_url };
        setForm(f);
      }
      const { data } = await api.post("/tastings", toPayload(f));
      setSavedId(data.id);
      setEditing(false);
      onSaved && onSaved(data);
      toast.success(T.saved);
    } catch (e) {
      toast.error(e?.response?.data?.detail || T.err);
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-[#E5276A]/25 rounded-2xl shadow-sm overflow-hidden" data-testid="tasting-draft-card">
      <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-[#FCE4ED] to-white border-b border-[#E5276A]/15">
        <div className="relative w-14 h-20 rounded-lg bg-white flex items-center justify-center shrink-0 overflow-hidden">
          <Wine className="w-5 h-5 text-[#E5276A]/40 animate-pulse" />
          {form.photo_url && (
            <img key={form.photo_url} src={form.photo_url} alt="" data-testid="tasting-draft-photo"
                 className="absolute inset-0 w-full h-full object-contain bg-white"
                 onError={(e) => {
                   if (form.user_photo_url && form.photo_url !== form.user_photo_url) setForm((x) => ({ ...x, photo_url: x.user_photo_url }));
                   else e.currentTarget.style.display = "none";
                 }} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-[#E5276A] font-bold">{T.title}</div>
          <div className="font-display text-[#1F3A5F] leading-snug line-clamp-2" data-testid="tasting-draft-name">
            {form.name || "—"} {form.year ? <span className="text-[#8B95A1]">{form.year}</span> : null}
          </div>
        </div>
        {form.score !== "" && form.score != null && !editing && (
          <div className="text-sm font-bold text-[#E5276A] shrink-0" data-testid="tasting-draft-score">{form.score}/20</div>
        )}
        {!savedId && (
          <button type="button" onClick={() => setEditing((v) => !v)} data-testid="tasting-draft-edit-toggle"
                  className="text-xs inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-[#1F3A5F]/15 text-[#1F3A5F] hover:border-[#E5276A] hover:text-[#E5276A] transition-colors">
            {editing ? <Check className="w-3 h-3" /> : <Pencil className="w-3 h-3" />} {editing ? T.done : T.edit}
          </button>
        )}
      </div>

      {!savedId && (form.web_photo_url || form.user_photo_url) && (
        <div className="px-4 pt-3"><TastingPhotoControls form={form} setForm={setForm} lang={lang} testIdPrefix="tasting-draft-photo" /></div>
      )}
      <div className="px-4 py-3 space-y-2 text-sm">
        {editing ? (
          <>
            <div className="grid grid-cols-2 gap-2">
              {SHORT.map((k) => (
                <label key={k} className={k === "name" ? "col-span-2" : ""}>
                  <span className="text-[10px] uppercase tracking-wider text-[#8B95A1]">{T[k]}</span>
                  <input value={form[k] ?? ""} onChange={set(k)} data-testid={`tasting-draft-input-${k}`}
                         className="mt-0.5 w-full rounded-lg border border-[#1F3A5F]/15 px-2.5 py-1.5 text-sm text-[#1F3A5F] focus:border-[#E5276A] outline-none" />
                </label>
              ))}
            </div>
            {LONG.map((k) => (
              <label key={k} className="block">
                <span className="text-[10px] uppercase tracking-wider text-[#8B95A1]">{T[k]}</span>
                {isSuggested(k) && <SuggestBadge label={T.suggest} />}
                <textarea value={form[k] ?? ""} onChange={set(k)} rows={2} data-testid={`tasting-draft-input-${k}`}
                          className="mt-0.5 w-full rounded-lg border border-[#1F3A5F]/15 px-2.5 py-1.5 text-sm text-[#1F3A5F] focus:border-[#E5276A] outline-none resize-y" />
              </label>
            ))}
          </>
        ) : (
          <DraftSummary form={form} T={T} isSuggested={isSuggested} />
        )}
      </div>

      <div className="px-4 pb-4">
        {savedId ? (
          <button type="button" onClick={() => navigate(`/carnet?edit=${savedId}`)} data-testid="tasting-draft-view-carnet"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#1F3A5F] hover:bg-[#162B47] text-white text-sm font-semibold py-2.5 transition-colors">
            <NotebookPen className="w-4 h-4" /> {T.view} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        ) : (
          <button type="button" onClick={save} disabled={saving} data-testid="tasting-draft-save"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#E5276A] hover:bg-[#C01661] disabled:opacity-60 text-white text-sm font-semibold py-2.5 transition-colors">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {user ? T.save : T.login}
          </button>
        )}
      </div>
    </div>
  );
}

function SuggestBadge({ label }) {
  return (
    <span className="ml-1.5 first:ml-0 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-[#E5276A] bg-[#FCE4ED] rounded-full px-1.5 py-0.5 align-middle" data-testid="tasting-draft-suggest-badge">
      <Sparkles className="w-2.5 h-2.5" /> {label}
    </span>
  );
}

function DraftSummary({ form, T, isSuggested }) {
  const meta = [form.producer, form.appellation, form.region, form.grapes].filter(Boolean).join(" · ");
  return (
    <div className="space-y-1.5" data-testid="tasting-draft-summary">
      {meta && <div className="text-xs text-[#8B95A1]">{meta}</div>}
      {LONG.some(isSuggested) && <SuggestBadge label={T.suggest} />}
      {LONG.map((k) => form[k] ? (
        <div key={k}><span className="font-semibold text-[#1F3A5F]">{T[k]} : </span><span className="text-neutral-700">{form[k]}</span></div>
      ) : null)}
    </div>
  );
}
